/**
 * WHAT YOUR ORGANIZATION DECIDES, AND WHAT STANDINGDOCS DECIDES.
 *
 * One area at a time, chosen from a list down the left, in the order a
 * hospital administrator thinks about them: who sees how a policy was
 * approved, who sees the library's history, acknowledgements, what is
 * emailed, what a policy must have before it is published, and the short
 * list of rules that are not theirs to change.
 *
 * It used to be all of that on one page, every role list five checkboxes
 * tall. The settings are the same ones; what changed is that an administrator
 * can now see what kinds of setting exist before reading any of them.
 *
 * THE AREAS ARE NOT THE SERVER'S SECTIONS. The server keeps four documents
 * (visibility, notifications, governance, acknowledgements) and decides who
 * may edit each. An area here gathers settings by what they are ABOUT, so
 * Acknowledgements holds one visibility setting and two acknowledgement ones.
 * Each control is enabled by its own section's permission, and a save sends
 * each section only the settings that were changed in it.
 *
 * TWO AUDIENCES, ONE PAGE. The System Admin edits everything. An
 * Administrator sees everything and edits the publishing and acknowledgement
 * rules. Showing them the rest read-only is deliberate: they are the person
 * who will be asked why staff stopped seeing something.
 *
 * NOTHING HERE IS A SECURITY BOUNDARY. Every setting is enforced by a callable
 * or a Firestore rule; this only collects the decision.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../lib/callable';
import { useAuth } from '../../auth/useAuth';
import { PageState } from '../../shared/PageState';
import { Switch } from '../../shared/Switch';
import { Spinner } from '../../app/Spinner';
import {
  BOUNDS,
  GOVERNED_KEYS,
  GOVERNED_SECTIONS,
  ROLE_PLURAL,
  SETTABLE_ROLES,
  SETTING_LABELS,
  describeRoles,
  describeValue,
  invalidReminderDays,
  invalidRoleSet,
  RECOMMENDED,
  sameSetting,
  type GovernedSection,
  type SettableRole,
} from './orgGovernance';
import styles from '../../shared/Page.module.css';
import own from './OrgSettings.module.css';

interface Settings {
  orgName: string;
  settingsVersion: number;
  values: Record<GovernedSection, Record<string, unknown>>;
  needsInitialization: Record<GovernedSection, boolean>;
  editable: Record<GovernedSection, boolean>;
  safetyRules: string[];
}

function useGovernanceSettings() {
  return useQuery({
    queryKey: ['governance-settings'],
    queryFn: () => call<Record<string, never>, Settings>('getGovernanceSettings', {}),
  });
}

function useAfterChange() {
  const queryClient = useQueryClient();
  return () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['governance-settings'] }),
    // The pages that read the settings for themselves must not keep a stale
    // answer: a narrowed setting has to take effect in this tab, not at the next reload.
    queryClient.invalidateQueries({ queryKey: ['org-visibility'] }),
    queryClient.invalidateQueries({ queryKey: ['policies'] }),
  ]);
}

// ---------------------------------------------------------------- what is where

/** One setting: the server section that stores it, and its key there. */
export interface Ref { section: GovernedSection; key: string }
const at = (section: GovernedSection, key: string): Ref => ({ section, key });
const idOf = (r: Ref) => `${r.section}.${r.key}`;

export const AREA_IDS = ['approvals', 'library', 'acknowledgements', 'notifications', 'publishing', 'protections', 'defaults'] as const;
export type AreaId = (typeof AREA_IDS)[number];

interface AreaDef { id: AreaId; title: string; blurb: string; saveAs: string; settings: Ref[] }

/**
 * Every governed setting belongs to exactly one area; a test holds that, so
 * a setting cannot be added on the server and left with nowhere to be seen.
 */
export const AREAS: AreaDef[] = [
  {
    id: 'approvals', title: 'Approval records', saveAs: 'approval settings',
    blurb: 'Who can see how a policy was approved. This controls looking, never who may approve.',
    settings: [at('visibility', 'approvalRecordViewRoles'), at('visibility', 'approvalCommentViewRoles'), at('visibility', 'committeeEvidenceViewRoles')],
  },
  {
    id: 'library', title: 'Library visibility', saveAs: 'library settings',
    blurb: 'Who can reach more than the current version of a current policy.',
    settings: [at('visibility', 'versionHistoryViewRoles'), at('visibility', 'retiredPolicyViewRoles')],
  },
  {
    id: 'acknowledgements', title: 'Acknowledgements', saveAs: 'acknowledgement settings',
    blurb: 'Whether staff are asked to confirm they have read a policy, how long they get, and who can see who has.',
    settings: [at('acknowledgements', 'acknowledgementsEnabled'), at('acknowledgements', 'defaultAcknowledgementDays'), at('visibility', 'acknowledgementReportViewRoles')],
  },
  {
    id: 'notifications', title: 'Notifications', saveAs: 'notification settings',
    blurb: 'What StandingDocs emails your staff, when, and whether they may switch it off for themselves.',
    settings: [
      at('notifications', 'remindersEnabled'),
      at('notifications', 'acknowledgementRemindersEnabled'), at('notifications', 'acknowledgementRemindersMandatory'),
      at('notifications', 'reviewRemindersEnabled'), at('notifications', 'reviewRemindersMandatory'), at('notifications', 'reviewReminderDays'),
      at('acknowledgements', 'escalationEnabled'), at('acknowledgements', 'escalationAfterDaysLate'),
      at('notifications', 'stalledApprovalDigestEnabled'), at('notifications', 'stalledApprovalDigestMandatory'), at('notifications', 'stalledApprovalAfterDays'),
    ],
  },
  {
    id: 'publishing', title: 'Publishing rules', saveAs: 'publishing rules',
    blurb: 'What a policy must have before it reaches staff, and how often it comes back for review.',
    settings: [at('governance', 'defaultReviewIntervalMonths'), at('governance', 'enforceReviewDate'), at('governance', 'requireOwnerOnPublish'), at('governance', 'requirePolicyNumberOnPublish')],
  },
  { id: 'protections', title: 'StandingDocs protections', saveAs: '', blurb: '', settings: [] },
  { id: 'defaults', title: 'Restore defaults', saveAs: '', blurb: '', settings: [] },
];

/** "90, 60, 30" both ways, so the field holds what a person would type. */
function daysToText(value: unknown): string {
  return Array.isArray(value) ? value.join(', ') : '';
}
function textToDays(text: string): number[] {
  // Anything that is not plain digits is kept as "not a number", so the field is refused with a message.
  // Dropping it would save "90, 6O, 30" as 90 and 30 and lose a reminder without a word.
  return text.split(',').map((p) => p.trim()).filter(Boolean).map((p) => (/^\d+$/.test(p) ? Number(p) : Number.NaN));
}

function problemWith(r: Ref, value: unknown): string | null {
  if (r.section === 'visibility') return invalidRoleSet(value);
  if (r.key === 'reviewReminderDays') return invalidReminderDays(value);
  const bounds = (BOUNDS as Record<string, { min: number; max: number } | undefined>)[r.key];
  if (bounds) {
    if (typeof value !== 'number' || !Number.isInteger(value)) return 'must be a whole number';
    if (value < bounds.min || value > bounds.max) return `must be between ${bounds.min} and ${bounds.max}`;
  }
  return null;
}

/**
 * What a change to who-can-see would newly let people see, in a sentence
 * each. Narrowing needs no warning; widening does, because it cannot be
 * taken back from somebody who has already looked.
 */
export function widened(changes: Array<{ ref: Ref; from: unknown; to: unknown }>): string[] {
  const out: string[] = [];
  for (const c of changes) {
    if (c.ref.section !== 'visibility' || !Array.isArray(c.to)) continue;
    const before = Array.isArray(c.from) ? c.from as string[] : [];
    const added = (c.to as string[]).filter((r) => !before.includes(r));
    if (added.length === 0) continue;
    out.push(`${describeRoles(added)} will be able to see: ${SETTING_LABELS.visibility[c.ref.key]?.label.toLowerCase() ?? c.ref.key}.`);
  }
  return out;
}

// ---------------------------------------------------------------- the form state

/**
 * What somebody has changed and not yet saved, and nothing else.
 *
 * Only edited settings are held here; everything else is read from the
 * server's answer every time. So a save sends exactly what was changed, a
 * setting nobody touched can never be written back from a stale copy, and a
 * control hidden because the thing it depends on is off keeps its value.
 */
function useEdits(data: Settings) {
  const [edits, setEdits] = useState<Record<string, unknown>>({});
  const [texts, setTexts] = useState<Record<string, string>>({});
  const stored = (r: Ref) => data.values[r.section][r.key];
  const value = (r: Ref) => (idOf(r) in edits ? edits[idOf(r)] : stored(r));
  const changed = (r: Ref) => idOf(r) in edits && !sameSetting(edits[idOf(r)], stored(r));
  return {
    stored, value, changed,
    editable: (r: Ref) => data.editable[r.section],
    set: (r: Ref, v: unknown) => setEdits((e) => ({ ...e, [idOf(r)]: v })),
    text: (r: Ref) => texts[idOf(r)] ?? daysToText(stored(r)),
    setText: (r: Ref, t: string) => { setTexts((x) => ({ ...x, [idOf(r)]: t })); setEdits((e) => ({ ...e, [idOf(r)]: textToDays(t) })); },
    discard: (refs: Ref[]) => {
      const drop = new Set(refs.map(idOf));
      setEdits((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !drop.has(k))));
      setTexts((t) => Object.fromEntries(Object.entries(t).filter(([k]) => !drop.has(k))));
    },
    /** After a save: let go of each edit that is still what was sent. One changed again while the save was in flight is kept. */
    settle: (sent: Array<{ ref: Ref; to: unknown }>) => {
      setEdits((e) => {
        const next = { ...e };
        for (const c of sent) if (idOf(c.ref) in next && sameSetting(next[idOf(c.ref)], c.to)) delete next[idOf(c.ref)];
        return next;
      });
    },
    discardAll: () => { setEdits({}); setTexts({}); },
  };
}
type Edits = ReturnType<typeof useEdits>;

// ---------------------------------------------------------------- pieces

/** Says when a saved value is not the StandingDocs default, and offers the default back. One click: friction on the safe action is how a setting stays wrong. */
function DefaultNote({ r, form, onRestore, busy }: { r: Ref; form: Edits; onRestore: (r: Ref) => void; busy: boolean }) {
  if (sameSetting(form.stored(r), RECOMMENDED[r.section][r.key])) return null;
  const label = SETTING_LABELS[r.section][r.key]?.label ?? r.key;
  return (
    <p className={own.defaultNote}>
      Chosen by your organization. StandingDocs recommends {describeValue(r.section, r.key, RECOMMENDED[r.section][r.key])}.
      {form.editable(r) && (
        <>{' '}<button type="button" className={own.linkBtn} disabled={busy} aria-label={`Restore StandingDocs default for ${label}`} onClick={() => onRestore(r)}>Restore default</button></>
      )}
    </p>
  );
}

interface RowProps { r: Ref; form: Edits; busy: boolean; onRestore: (r: Ref) => void }

/** A setting that is on or off: its name and what it does on the left, the switch on the right. */
function ToggleRow({ r, form, busy, onRestore, nested = false }: RowProps & { nested?: boolean }) {
  const id = useId();
  const label = SETTING_LABELS[r.section][r.key]!;
  // The "mandatory" settings are stored as "staff may NOT switch it off" and asked as "staff MAY".
  const inverted = r.key.endsWith('Mandatory');
  const on = inverted ? form.value(r) !== true : form.value(r) === true;
  return (
    <div className={nested ? `${own.row} ${own.nested}` : own.row}>
      <div className={own.rowText}>
        <span className={own.rowLabel} id={`${id}-l`}>{label.label}</span>
        <p className={own.help} id={`${id}-h`}>{label.help}</p>
        <DefaultNote r={r} form={form} onRestore={onRestore} busy={busy} />
      </div>
      <Switch
        checked={on} disabled={busy || !form.editable(r)} labelledBy={`${id}-l`} describedBy={`${id}-h`}
        onText={inverted ? 'Yes' : 'On'} offText={inverted ? 'No' : 'Off'}
        onChange={(next) => form.set(r, inverted ? !next : next)}
      />
    </div>
  );
}

/** A number of days or months: a small box, because it holds two or three digits. */
function NumberRow({ r, form, busy, onRestore, unit, nested = false }: RowProps & { unit: string; nested?: boolean }) {
  const id = useId();
  const label = SETTING_LABELS[r.section][r.key]!;
  const bounds = (BOUNDS as Record<string, { min: number; max: number } | undefined>)[r.key];
  const value = form.value(r);
  const problem = form.changed(r) ? problemWith(r, value) : null;
  return (
    <div className={nested ? `${own.row} ${own.nested}` : own.row}>
      <div className={own.rowText}>
        <label className={own.rowLabel} htmlFor={id}>{label.label}</label>
        <p className={own.help} id={`${id}-h`}>{label.help}</p>
        {problem && <p className={own.problem} id={`${id}-e`} role="alert">{label.label} {problem}.</p>}
        <DefaultNote r={r} form={form} onRestore={onRestore} busy={busy} />
      </div>
      <span className={own.numberBox}>
        <input
          id={id} type="number" className={own.number} value={typeof value === 'number' ? value : ''} min={bounds?.min} max={bounds?.max}
          disabled={busy || !form.editable(r)} aria-describedby={problem ? `${id}-e ${id}-h` : `${id}-h`} aria-invalid={problem ? true : undefined}
          onChange={(e) => { const n = e.target.value === '' ? NaN : Number(e.target.value); form.set(r, Number.isFinite(n) ? n : null); }}
        />
        <span className={own.unit}>{unit}</span>
      </span>
    </div>
  );
}

/** "90, 60, 30": a short list of days, typed. */
function DaysRow({ r, form, busy, onRestore }: RowProps) {
  const id = useId();
  const label = SETTING_LABELS[r.section][r.key]!;
  const problem = form.changed(r) ? problemWith(r, form.value(r)) : null;
  return (
    <div className={`${own.row} ${own.nested}`}>
      <div className={own.rowText}>
        <label className={own.rowLabel} htmlFor={id}>{label.label}</label>
        <p className={own.help} id={`${id}-h`}>{label.help}</p>
        {problem && <p className={own.problem} id={`${id}-e`} role="alert">{label.label} {problem}.</p>}
        <DefaultNote r={r} form={form} onRestore={onRestore} busy={busy} />
      </div>
      <span className={own.numberBox}>
        <input
          id={id} type="text" inputMode="numeric" className={own.days} value={form.text(r)} maxLength={BOUNDS.reviewReminderDays.maxEntries * 5}
          disabled={busy || !form.editable(r)} aria-describedby={problem ? `${id}-e ${id}-h` : `${id}-h`} aria-invalid={problem ? true : undefined}
          onChange={(e) => form.setText(r, e.target.value)}
        />
        <span className={own.unit}>days ahead</span>
      </span>
    </div>
  );
}

/**
 * Who can see what: roles down the side, the things they may see across the
 * top. For settings that are the same question asked of the same five roles,
 * so that reading across a row answers "what can a Reader see?" and reading
 * down a column answers "who can see the comments?".
 */
function RoleMatrix({ caption, columns, form, busy, onRestore }: { caption: string; columns: Ref[]; form: Edits; busy: boolean; onRestore: (r: Ref) => void }) {
  const id = useId();
  const toggle = (r: Ref, role: SettableRole, on: boolean) => {
    const now = Array.isArray(form.value(r)) ? form.value(r) as string[] : [];
    form.set(r, on ? [...SETTABLE_ROLES].filter((x) => x === role || now.includes(x)) : now.filter((x) => x !== role));
  };
  return (
    <div className={own.matrixBlock}>
      <dl className={own.meanings}>
        {columns.map((r) => (
          <div key={idOf(r)} className={own.meaning}>
            <dt>{SETTING_LABELS[r.section][r.key]!.label}</dt>
            <dd id={`${id}-${r.key}-h`}>
              {SETTING_LABELS[r.section][r.key]!.help}
              <DefaultNote r={r} form={form} onRestore={onRestore} busy={busy} />
            </dd>
          </div>
        ))}
      </dl>
      <table className={own.matrix}>
        <caption className="vh">{caption}</caption>
        <thead>
          <tr>
            <th scope="col" className={own.matrixRole}>Role</th>
            {columns.map((r) => <th key={idOf(r)} scope="col">{SETTING_LABELS[r.section][r.key]!.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {SETTABLE_ROLES.map((role) => (
            <tr key={role}>
              <th scope="row" className={own.matrixRole}>{ROLE_PLURAL[role]}</th>
              {columns.map((r) => {
                const value = Array.isArray(form.value(r)) ? form.value(r) as string[] : [];
                return (
                  <td key={idOf(r)}>
                    <input
                      type="checkbox" checked={value.includes(role)} disabled={busy || !form.editable(r)}
                      aria-label={`${ROLE_PLURAL[role]} can see: ${SETTING_LABELS[r.section][r.key]!.label}`}
                      onChange={(e) => toggle(r, role, e.target.checked)}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {columns.map((r) => {
        const problem = form.changed(r) ? problemWith(r, form.value(r)) : null;
        return problem ? <p key={idOf(r)} className={own.problem} role="alert">{SETTING_LABELS[r.section][r.key]!.label} {problem}.</p> : null;
      })}
      <p className={own.help}>Your System Admin can always see all of these and is not listed.</p>
    </div>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={own.group}>
      <h3 className={own.groupTitle}>{title}</h3>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- an area

function AreaBody({ area, form, busy, onRestore }: { area: AreaDef; form: Edits; busy: boolean; onRestore: (r: Ref) => void }) {
  const p = { form, busy, onRestore };
  const on = (section: GovernedSection, key: string) => form.value(at(section, key)) === true;
  switch (area.id) {
    case 'approvals':
      return <RoleMatrix caption="Who can see approval records, comments and committee evidence" columns={area.settings} {...p} />;
    case 'library':
      return <RoleMatrix caption="Who can see earlier versions and retired policies" columns={area.settings} {...p} />;
    case 'acknowledgements':
      return (
        <>
          <Group title="Asking staff to acknowledge">
            <ToggleRow r={at('acknowledgements', 'acknowledgementsEnabled')} {...p} />
            {/* Only when it applies. What was set stays set while it is hidden. */}
            {on('acknowledgements', 'acknowledgementsEnabled') && <NumberRow r={at('acknowledgements', 'defaultAcknowledgementDays')} unit="days" nested {...p} />}
          </Group>
          <Group title="Who can see who has acknowledged">
            <RoleMatrix caption="Who can see the list of staff who have and have not acknowledged" columns={[at('visibility', 'acknowledgementReportViewRoles')]} {...p} />
          </Group>
        </>
      );
    case 'notifications': {
      const master = on('notifications', 'remindersEnabled');
      return (
        <>
          <ToggleRow r={at('notifications', 'remindersEnabled')} {...p} />
          {!master && <p className={own.help}>Reminder email is off, so nothing below is sent. Turn it on to choose which reminders go out.</p>}
          {master && (
            <>
              <Group title="Acknowledgements">
                <ToggleRow r={at('notifications', 'acknowledgementRemindersEnabled')} {...p} />
                {on('notifications', 'acknowledgementRemindersEnabled') && <ToggleRow r={at('notifications', 'acknowledgementRemindersMandatory')} nested {...p} />}
              </Group>
              <Group title="Reviews">
                <ToggleRow r={at('notifications', 'reviewRemindersEnabled')} {...p} />
                {on('notifications', 'reviewRemindersEnabled') && (
                  <>
                    <DaysRow r={at('notifications', 'reviewReminderDays')} {...p} />
                    <ToggleRow r={at('notifications', 'reviewRemindersMandatory')} nested {...p} />
                  </>
                )}
                <ToggleRow r={at('acknowledgements', 'escalationEnabled')} {...p} />
                {on('acknowledgements', 'escalationEnabled') && <NumberRow r={at('acknowledgements', 'escalationAfterDaysLate')} unit="days overdue" nested {...p} />}
              </Group>
              <Group title="Approvals">
                <ToggleRow r={at('notifications', 'stalledApprovalDigestEnabled')} {...p} />
                {on('notifications', 'stalledApprovalDigestEnabled') && (
                  <>
                    <NumberRow r={at('notifications', 'stalledApprovalAfterDays')} unit="days" nested {...p} />
                    <ToggleRow r={at('notifications', 'stalledApprovalDigestMandatory')} nested {...p} />
                  </>
                )}
              </Group>
            </>
          )}
        </>
      );
    }
    case 'publishing':
      return (
        <>
          <NumberRow r={at('governance', 'defaultReviewIntervalMonths')} unit="months" {...p} />
          <ToggleRow r={at('governance', 'enforceReviewDate')} {...p} />
          <ToggleRow r={at('governance', 'requireOwnerOnPublish')} {...p} />
          <ToggleRow r={at('governance', 'requirePolicyNumberOnPublish')} {...p} />
        </>
      );
    default:
      return null;
  }
}

/**
 * Which settings depend on which switch being on. A setting whose switch is
 * off is not on the screen, so an edit to it is not part of what Save sends:
 * nobody should save a number they can no longer see. The edit is kept, and
 * is there again if the switch goes back on.
 */
const NEEDS: Record<string, Ref[]> = {
  'acknowledgements.defaultAcknowledgementDays': [at('acknowledgements', 'acknowledgementsEnabled')],
  'notifications.acknowledgementRemindersEnabled': [at('notifications', 'remindersEnabled')],
  'notifications.acknowledgementRemindersMandatory': [at('notifications', 'remindersEnabled'), at('notifications', 'acknowledgementRemindersEnabled')],
  'notifications.reviewRemindersEnabled': [at('notifications', 'remindersEnabled')],
  'notifications.reviewRemindersMandatory': [at('notifications', 'remindersEnabled'), at('notifications', 'reviewRemindersEnabled')],
  'notifications.reviewReminderDays': [at('notifications', 'remindersEnabled'), at('notifications', 'reviewRemindersEnabled')],
  'acknowledgements.escalationEnabled': [at('notifications', 'remindersEnabled')],
  'acknowledgements.escalationAfterDaysLate': [at('notifications', 'remindersEnabled'), at('acknowledgements', 'escalationEnabled')],
  'notifications.stalledApprovalDigestEnabled': [at('notifications', 'remindersEnabled')],
  'notifications.stalledApprovalDigestMandatory': [at('notifications', 'remindersEnabled'), at('notifications', 'stalledApprovalDigestEnabled')],
  'notifications.stalledApprovalAfterDays': [at('notifications', 'remindersEnabled'), at('notifications', 'stalledApprovalDigestEnabled')],
};
export function onScreen(r: Ref, value: (r: Ref) => unknown): boolean {
  return (NEEDS[idOf(r)] ?? []).every((need) => value(need) === true);
}

function Area({ area, data, form }: { area: AreaDef; data: Settings; form: Edits }) {
  const after = useAfterChange();
  const [confirming, setConfirming] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null);
  const changes = area.settings.filter((r) => form.changed(r) && onScreen(r, form.value)).map((r) => ({ ref: r, from: form.stored(r), to: form.value(r) }));
  const problems = changes.filter((c) => problemWith(c.ref, c.to) !== null);
  const dirty = changes.length > 0;
  const broader = widened(changes);

  const save = useMutation({
    // One request per server section, each carrying only what changed in it.
    mutationFn: async () => {
      const sent = changes;
      const bySection = new Map<GovernedSection, { values: Record<string, unknown>; expected: Record<string, unknown> }>();
      for (const c of sent) {
        const s = bySection.get(c.ref.section) ?? { values: {}, expected: {} };
        s.values[c.ref.key] = c.to;
        // What this page was showing, so the server can refuse a change built on a value somebody else has since changed.
        s.expected[c.ref.key] = c.from;
        bySection.set(c.ref.section, s);
      }
      let saved = 0;
      for (const [section, s] of bySection) {
        try {
          await call('updateGovernanceSection', { section, values: s.values, expected: s.expected });
          saved += 1;
        } catch (e) {
          // With two sections in one save, the first may already be written. Say so; the page reads the truth again either way.
          const message = isCallFailure(e) ? e.message : 'Those settings could not be saved.';
          throw Object.assign(new Error(saved > 0 ? `${message} Some of the other changes on this page were saved before that.` : message), { conflict: isCallFailure(e) && e.code === 'aborted' });
        }
      }
      return sent;
    },
    onMutate: () => setConflict(null),
    // Whatever happened, read the settings again.
    onSettled: async () => { await after(); },
    onSuccess: (sent) => { form.settle(sent); setConfirming(false); },
    onError: (e) => {
      setConfirming(false);
      // Built on values that are no longer true. Let go of them, so what is on screen is what is stored and the change is made again on that.
      if ((e as { conflict?: boolean }).conflict) { form.discard(area.settings); setConflict(e.message); }
    },
  });
  const restore = useMutation({
    mutationFn: (r: Ref) => call('resetGovernanceSetting', { section: r.section, key: r.key }),
    onSuccess: async (_d, r) => { await after(); form.discard([r]); },
  });
  const busy = save.isPending || restore.isPending;
  const readOnly = [...new Set(area.settings.map((r) => r.section))].filter((s) => !data.editable[s]);
  const mayEditAny = area.settings.some(form.editable);

  return (
    <>
      <p className={own.blurb}>{area.blurb}</p>
      {readOnly.length > 0 && (
        <div className={`${styles.notice} ${styles.noticeInfo}`} role="note">
          {mayEditAny
            ? 'Some of these are set by your organization’s System Admin. You can see what has been chosen.'
            : 'Your organization’s System Admin sets these. You can see what has been chosen.'}
        </div>
      )}
      {(conflict || save.error) && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {conflict ?? (save.error instanceof Error ? save.error.message : 'Those settings could not be saved.')}
        </div>
      )}
      {restore.error && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {isCallFailure(restore.error) ? restore.error.message : 'That setting could not be restored.'}
        </div>
      )}

      <AreaBody area={area} form={form} busy={busy} onRestore={(r) => restore.mutate(r)} />

      {mayEditAny && (
        <div className={own.actions}>
          {confirming && broader.length > 0 ? (
            <div className={own.confirm} role="group" aria-label="Confirm wider access">
              <p className={own.confirmTitle} role="alert">This lets more people see more.</p>
              <ul className={own.confirmList}>{broader.map((b) => <li key={b}>{b}</li>)}</ul>
              <p className={own.help}>It takes effect as soon as it is saved, and is recorded with your name.</p>
              <div className={own.buttons}>
                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save and widen access'}</button>
                <button type="button" className={styles.btn} disabled={busy} autoFocus onClick={() => setConfirming(false)}>Go back</button>
              </div>
            </div>
          ) : (
            <>
              <p className={own.status} role="status">
                {dirty ? <span className={own.unsaved}>Unsaved changes</span> : save.isSuccess ? 'Saved.' : ''}
              </p>
              <div className={own.buttons}>
                <button type="button" className={styles.btn} disabled={!dirty || busy} onClick={() => { form.discard(area.settings); save.reset(); setConflict(null); }}>Discard changes</button>
                <button
                  type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={!dirty || problems.length > 0 || busy}
                  onClick={() => (broader.length > 0 ? setConfirming(true) : save.mutate())}
                >
                  {save.isPending ? 'Saving…' : `Save ${area.saveAs}`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

/** What is not a setting: said plainly, as a list, with nothing that looks like a control. */
function Protections({ rules }: { rules: string[] }) {
  return (
    <>
      <p className={own.blurb}>These rules are enforced by StandingDocs for every organization. They are not settings, and nobody can switch them off, including us.</p>
      <ul className={own.rules}>
        {rules.map((rule) => <li key={rule}>{rule}</li>)}
      </ul>
    </>
  );
}

/**
 * The bulk restore. Typed confirmation and a reason, because it overwrites
 * decisions somebody made deliberately.
 */
function RestoreAll({ onRestored }: { onRestored: () => void }) {
  const after = useAfterChange();
  const restoreAll = useMutation({
    mutationFn: (input: { confirmText: string; reason: string }) => call('resetAllGovernanceSettings', input),
    // Every setting has just been replaced, so an edit still held from before would be an edit to something that no longer exists.
    onSuccess: async () => { onRestored(); await after(); },
  });
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  return (
    <>
      <p className={own.blurb}>
        Puts every setting on these pages back to what StandingDocs recommends today. Choices your organization has made are overwritten.
        This is recorded with your name, the time, and what each value was before. To put back one setting, use Restore default beside it.
      </p>
      {restoreAll.error && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {isCallFailure(restoreAll.error) ? restoreAll.error.message : 'Nothing was restored.'}
        </div>
      )}
      {restoreAll.isSuccess && <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">Every setting is back to the StandingDocs default.</div>}
      <div className={own.restoreForm}>
        <label className={own.rowLabel} htmlFor="restore-reason">Why</label>
        <input id="restore-reason" className={own.text} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
        <label className={own.rowLabel} htmlFor="restore-confirm">Type CONFIRM</label>
        <input id="restore-confirm" className={own.days} value={confirmText} maxLength={128} onChange={(e) => setConfirmText(e.target.value)} />
        <div className={own.buttons} style={{ justifyContent: 'flex-start' }}>
          <button
            type="button" className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={confirmText.toUpperCase() !== 'CONFIRM' || reason.trim().length < 5 || restoreAll.isPending}
            onClick={() => restoreAll.mutate({ confirmText, reason }, { onSuccess: () => { setConfirmText(''); setReason(''); } })}
          >
            {restoreAll.isPending ? 'Restoring…' : 'Restore every setting'}
          </button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- the page

function Loaded({ data }: { data: Settings }) {
  const { session } = useAuth();
  const [params] = useSearchParams();
  const form = useEdits(data);
  const mayRestoreAll = GOVERNED_SECTIONS.every((s) => data.editable[s]);
  const areas = AREAS.filter((a) => a.id !== 'defaults' || mayRestoreAll);
  const asked = params.get('section');
  const area = areas.find((a) => a.id === asked) ?? areas[0]!;

  // Choosing an area moves focus to its heading, so a keyboard or screen-reader user lands in what they chose and not back at the top of the list.
  const heading = useRef<HTMLHeadingElement>(null);
  const shown = useRef(area.id);
  useEffect(() => {
    if (shown.current === area.id) return;
    shown.current = area.id;
    heading.current?.focus();
  }, [area.id]);

  const pending = GOVERNED_SECTIONS.filter((s) => data.needsInitialization[s]);

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>Organization settings</h1>
        <p className={styles.subtitle}>
          Configure approval records, visibility, acknowledgements, notifications and publishing rules{data.orgName ? ` for ${data.orgName}` : ''}.
        </p>
      </header>

      <div className={`${styles.body} ${own.layout}`}>
        <nav className={own.nav} aria-label="Settings areas">
          <ul>
            {areas.map((a) => {
              const unsaved = a.settings.some((r) => form.changed(r) && onScreen(r, form.value));
              return (
                <li key={a.id} className={a.id === 'protections' ? own.navBreak : undefined}>
                  <Link
                    to={a.id === areas[0]!.id ? '.' : `?section=${a.id}`} replace
                    className={a.id === area.id ? `${own.navLink} ${own.navOn}` : own.navLink}
                    aria-current={a.id === area.id ? 'page' : undefined}
                  >
                    <span>{a.title}</span>
                    {unsaved && <span className={own.navUnsaved}>Unsaved</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <section className={own.content} aria-labelledby="settings-area-title">
          <h2 className={own.areaTitle} id="settings-area-title" tabIndex={-1} ref={heading}>{area.title}</h2>
          {pending.length > 0 && area.settings.some((r) => pending.includes(r.section)) && (
            <div className={`${styles.notice} ${styles.noticeInfo}`} role="note">
              Some of these have not been recorded for your organization yet, so they are showing
              what StandingDocs has always done. Saving one records it. Nothing changes until you do.
            </div>
          )}
          {area.id === 'protections' ? <Protections rules={data.safetyRules} />
            : area.id === 'defaults' ? <RestoreAll onRestored={form.discardAll} />
              // Keyed, so the confirmation and the "Saved." of one area do not follow you into another. What was edited is held above and stays.
              : <Area key={area.id} area={area} data={data} form={form} />}
          {session?.roles.includes('sysadmin') !== true && area.id !== 'protections' && (
            <p className={own.help} style={{ marginTop: 'var(--sp-5)' }}>
              Who sees approval records, what is in the library and what is emailed are set by your organization&rsquo;s System Admin.
            </p>
          )}
        </section>
      </div>
    </>
  );
}

export function OrgSettingsPage() {
  const { data, isLoading, error } = useGovernanceSettings();
  if (isLoading) return <Spinner label="Loading settings" />;
  // Only when there is nothing to show. A failed re-read in the background must not take the page, and what was being edited, away.
  if (!data) {
    return (
      <PageState title="Could not load settings" tone="alert">
        {isCallFailure(error) ? error.message : 'Something went wrong.'}
      </PageState>
    );
  }
  return <Loaded data={data} />;
}

/** Every governed key, for the test that holds each one to exactly one area. */
export const ALL_GOVERNED: Ref[] = GOVERNED_SECTIONS.flatMap((s) => GOVERNED_KEYS[s].map((k) => at(s, k)));
